'use client';

import Link from 'next/link';
import { products, type Product } from '@/lib/products';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { productsIndexCopy } from '@/lib/i18n/pages/productsIndex';
import { cn } from '@/lib/utils';
import { BrowserShot, PhoneShot, Tilt } from '@/components/products/shots';
import { Reveal } from '@/components/products/Reveal';
import {
  AccentGlow,
  CtaRow,
  FeatureRows,
  FloatChip,
  SectionHead,
} from '@/components/products/blocks';

const SITE_ACCENT = '#34d399';

/** Mono index + name + tagline + blurb + chips + explore link. */
function ProductCopy({ product }: { product: Product }) {
  const { locale, href } = useLocale();
  const t = pageCopy(productsIndexCopy, locale);
  const isEn = locale === 'en';
  const text = isEn
    ? {
        tagline: product.tagline,
        blurb: product.blurb,
        chips: product.chips,
        explore: `Explore ${product.name}`,
      }
    : t.items[product.slug];
  return (
    <div className="max-w-xl">
      <span
        className="font-mono text-[11px] uppercase tracking-[0.3em]"
        style={{ color: product.accent }}
      >
        <bdi dir="ltr">
          {product.index} · {product.domain}
        </bdi>
      </span>
      <h3 className="keep-latin mt-5 font-six-caps text-7xl lowercase leading-[0.88] text-white sm:text-8xl">
        <bdi dir="ltr">{product.name}</bdi>
      </h3>
      <p className="mt-5 text-xl font-light leading-snug text-white md:text-2xl">
        {text.tagline}
      </p>
      <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-white/55 md:text-base">
        {text.blurb}
      </p>
      <ul className="mt-7 flex flex-wrap gap-2.5" aria-label={t.highlights}>
        {text.chips.map((chip) => (
          <li
            key={chip}
            className="rounded-full border border-white/12 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 rtl:tracking-normal"
          >
            {chip}
          </li>
        ))}
      </ul>
      <Link
        href={href(`/products/${product.slug}`)}
        data-cursor-label={t.cursorView}
        className="group mt-9 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white rtl:tracking-normal"
      >
        <span
          className="border-b pb-1 transition-colors duration-300"
          style={{ borderColor: product.accent }}
        >
          {text.explore}
        </span>
        <span
          aria-hidden="true"
          className="rtl-mirror transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-1 rtl:group-hover:-translate-x-1"
          style={{ color: product.accent }}
        >
          →
        </span>
      </Link>
    </div>
  );
}

/** Final CTA band. Same markup as the shared DemoBand, but the title and
 * ghost word can take the RTL display classes (Six Caps sizes are wrong for
 * Arabic script) and the eyebrow copy stays translated. */
function DemoBandLocal({
  ghost,
  title,
  sub,
  ctaLabel,
  ctaHref,
  eyebrow,
  accent,
  note,
  rtl,
}: {
  ghost: string;
  title: string;
  sub: string;
  ctaLabel: string;
  ctaHref: string;
  eyebrow: string;
  accent: string;
  note: string;
  rtl: boolean;
}) {
  return (
    <section className="relative overflow-hidden border-t border-white/10 py-28 md:py-36">
      <span
        aria-hidden="true"
        className={cn(
          'pv-ghost pointer-events-none absolute -bottom-8 left-1/2 w-full -translate-x-1/2 select-none text-center font-six-caps text-[24vw] uppercase leading-none',
          rtl && 'rtl-display-l',
        )}
      >
        {ghost}
      </span>
      <div className="relative">
        <span className="eyebrow" style={{ color: accent }}>
          {eyebrow}
        </span>
        <h2
          className={cn(
            'mt-6 max-w-4xl font-six-caps text-6xl lowercase leading-[0.9] text-white sm:text-7xl md:text-8xl',
            rtl && 'rtl-display-l',
          )}
        >
          {title}
        </h2>
        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/65 md:text-lg">
          {sub}
        </p>
        <CtaRow primary={{ label: ctaLabel, href: ctaHref }} accent={accent} className="mt-10" />
        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
          <bdi dir="ltr">{note}</bdi>
        </p>
      </div>
    </section>
  );
}

export function ProductsIndexClient() {
  const [crm, erp, menu] = products;
  const { locale, href } = useLocale();
  const t = pageCopy(productsIndexCopy, locale);
  const isEn = locale === 'en';
  const alt = (p: Product) => (isEn ? p.imageAlt : t.items[p.slug].imageAlt);

  return (
    <div className="self-styled relative text-white">
      {/* ── Hero: triptych of the three products ─────────────────────── */}
      <section className="relative flex min-h-[100dvh] flex-col justify-center overflow-hidden px-6 pb-20 pt-[clamp(7rem,12vw,9rem)] md:px-12 lg:px-24">
        <div className="mx-auto grid w-full max-w-[1400px] items-center gap-14 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
          <div>
            <h1
              className="pv-load rtl-display-l font-six-caps text-[clamp(4rem,9vw,8.5rem)] lowercase leading-[0.88]"
              style={{ animationDelay: '80ms' }}
            >
              {t.hero.title}
            </h1>
            <p
              className="pv-load mt-8 max-w-xl text-base font-light leading-relaxed text-white/65 md:text-lg"
              style={{ animationDelay: '220ms' }}
            >
              {t.hero.sub}
            </p>
            <div className="pv-load" style={{ animationDelay: '360ms' }}>
              <CtaRow
                primary={{ label: t.hero.bookDemo, href: href('/contact') }}
                secondary={{ label: t.hero.lineup, href: '#lineup' }}
                accent={SITE_ACCENT}
                className="mt-10"
              />
            </div>
          </div>

          {/* Desktop triptych */}
          <div className="pv-load relative hidden h-[560px] lg:block" style={{ animationDelay: '300ms' }}>
            <AccentGlow accent={SITE_ACCENT} className="left-1/3 top-1/4 h-80 w-80" opacity={0.14} />
            <div className="absolute start-0 top-24 w-[64%] -rotate-3 opacity-80 rtl:rotate-3">
              <BrowserShot
                src={erp.image}
                alt={alt(erp)}
                url={erp.domain}
                accent={erp.accent}
                aspect="aspect-[16/10]"
                sizes="40vw"
              />
            </div>
            <div className="absolute end-0 top-0 w-[70%] rotate-2 rtl:-rotate-2">
              <Tilt>
                <BrowserShot
                  src={crm.image}
                  alt={alt(crm)}
                  url={crm.domain}
                  accent={crm.accent}
                  aspect="aspect-[16/10]"
                  priority
                  sizes="45vw"
                />
              </Tilt>
            </div>
            <div className="absolute -bottom-2 end-8 w-[24%] rotate-6 rtl:-rotate-6">
              <PhoneShot src={menu.image} alt={alt(menu)} priority sizes="15vw" />
            </div>
          </div>

          {/* Mobile hero shot */}
          <div className="pv-load lg:hidden" style={{ animationDelay: '300ms' }}>
            <BrowserShot
              src={crm.image}
              alt={alt(crm)}
              url={crm.domain}
              accent={crm.accent}
              priority
            />
          </div>
        </div>
      </section>

      {/* ── 01 · DilaCRM — split world ───────────────────────────────── */}
      <section
        id="lineup"
        aria-label="DilaCRM"
        className="relative border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24"
      >
        <span
          aria-hidden="true"
          className="pv-ghost pointer-events-none absolute -top-6 end-0 select-none font-six-caps text-[20vw] leading-none"
        >
          01
        </span>
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <ProductCopy product={crm} />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={crm.accent} className="-left-10 top-1/3 h-72 w-72" />
            <div className="relative rotate-1">
              <Tilt>
                <BrowserShot
                  src={crm.image}
                  alt={alt(crm)}
                  url={crm.domain}
                  accent={crm.accent}
                  sizes="(max-width: 1024px) 100vw, 55vw"
                />
              </Tilt>
            </div>
            <FloatChip accent={crm.accent} className="-left-4 top-8" dot>
              <bdi dir="ltr">WhatsApp</bdi>
            </FloatChip>
            <FloatChip accent={crm.accent} className="-right-3 top-1/3" alt>
              <bdi dir="ltr">Instagram</bdi>
            </FloatChip>
            <FloatChip accent={crm.accent} className="bottom-10 left-1/4">
              {isEn ? 'TikTok · Telegram · Email' : t.crm.chipEmail}
            </FloatChip>
          </Reveal>
        </div>
      </section>

      {/* ── 02 · DilaERP — copy split + full-width band ──────────────── */}
      <section
        aria-label="DilaERP"
        className="relative border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24"
      >
        <span
          aria-hidden="true"
          className="pv-ghost pointer-events-none absolute -top-6 start-0 select-none font-six-caps text-[20vw] leading-none"
        >
          02
        </span>
        <div className="mx-auto max-w-[1400px]">
          <div className="grid items-end gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-6">
              <ProductCopy product={erp} />
            </Reveal>
            <Reveal className="lg:col-span-6" delay={100}>
              <ul className="flex flex-col gap-5 border-s border-white/10 ps-8">
                {t.erp.bullets.map((line) => (
                  <li key={line} className="text-lg font-light leading-snug text-white/75">
                    {line}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal className="relative mt-16" delay={150}>
            <AccentGlow accent={erp.accent} className="right-1/4 -top-10 h-72 w-72" />
            <Tilt>
              <BrowserShot
                src={erp.image}
                alt={alt(erp)}
                url={erp.domain}
                accent={erp.accent}
                aspect="aspect-[16/9]"
                sizes="(max-width: 1024px) 100vw, 85vw"
              />
            </Tilt>
            <FloatChip accent={erp.accent} className="left-10 top-10" dot>
              {t.erp.chipProfit}
            </FloatChip>
            <FloatChip accent={erp.accent} className="right-16 top-24" alt>
              {t.erp.chipPayroll}
            </FloatChip>
            <FloatChip accent={erp.accent} className="bottom-12 right-1/3">
              {isEn ? 'POS · Stock · CRM' : t.erp.chipPos}
            </FloatChip>
          </Reveal>
        </div>
      </section>

      {/* ── 03 · dila.menu — phone fan over food ─────────────────────── */}
      <section
        aria-label="dila.menu"
        className="relative border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24"
      >
        <span
          aria-hidden="true"
          className="pv-ghost pointer-events-none absolute -top-6 end-0 select-none font-six-caps text-[20vw] leading-none"
        >
          03
        </span>
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <ProductCopy product={menu} />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={menu.accent} className="left-1/3 top-1/4 h-80 w-80" opacity={0.18} />
            <div className="relative mx-auto h-[540px] max-w-[560px] sm:h-[600px]">
              {/* Food backdrop card */}
              <div
                className="absolute inset-x-8 top-6 bottom-6 -rotate-2 overflow-hidden rounded-2xl border border-white/10 bg-cover bg-center opacity-45"
                style={{ backgroundImage: 'url(/images/products/dilamenu/food-koshky.webp)' }}
                aria-hidden="true"
              />
              <div className="absolute start-0 top-14 hidden w-[34%] -rotate-[8deg] sm:block rtl:rotate-[8deg]">
                <PhoneShot
                  src="/images/products/dilamenu/welcome.webp"
                  alt={t.menu.welcomeAlt}
                  sizes="18vw"
                />
              </div>
              <div className="absolute left-1/2 top-0 z-10 w-[46%] -translate-x-1/2 sm:w-[42%]">
                <PhoneShot
                  src={menu.image}
                  alt={alt(menu)}
                  priority
                  sizes="(max-width: 640px) 46vw, 22vw"
                />
              </div>
              <div className="absolute end-0 top-20 hidden w-[32%] rotate-[8deg] sm:block rtl:-rotate-[8deg]">
                <PhoneShot
                  src="/images/products/dilamenu/item-modal.webp"
                  alt={t.menu.modalAlt}
                  sizes="17vw"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Why ours is different ────────────────────────────────────── */}
      <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              kicker={t.why.kicker}
              title={t.why.title}
              lede={t.why.lede}
              accent={SITE_ACCENT}
              titleClassName="rtl-display-m"
            />
          </Reveal>
          <Reveal delay={120}>
            <FeatureRows
              accent={SITE_ACCENT}
              className="mt-14"
              rows={t.why.rows}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Demo band ────────────────────────────────────────────────── */}
      <div className="px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <DemoBandLocal
            ghost={t.demo.ghost}
            title={t.demo.title}
            sub={t.demo.sub}
            ctaLabel={t.demo.cta}
            ctaHref={href('/contact')}
            eyebrow={t.demo.eyebrow}
            accent={SITE_ACCENT}
            note="crm.dilaweb.com · app.datacode.app · dila.menu"
            rtl={!isEn}
          />
        </div>
      </div>
    </div>
  );
}
